import { serverClock } from '@mezha/api'
import { useEffect, useRef, useState } from 'react'

/** Re-renders every `ms` while mounted; returns the server time. */
export function useServerNow(ms = 1000): number {
  const [now, setNow] = useState(() => serverClock.now())
  useEffect(() => {
    const t = setInterval(() => setNow(serverClock.now()), ms)
    return () => clearInterval(t)
  }, [ms])
  return now
}

/** "14:32" in the player's zone. */
export const clockText = (iso: string | number | Date): string =>
  new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })

/** "1:05", "0:07", "2 ч 10 мин" for a duration. */
export function durationText(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000))
  if (s >= 3600) {
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    return `${h} ч ${m} мин`
  }
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Russian plural: plural(3, 'бой', 'боя', 'боёв'). */
export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100
  const b = a % 10
  if (a > 10 && a < 20) return many
  if (b > 1 && b < 5) return few
  if (b === 1) return one
  return many
}

/** Calls `refetch` once when the server moment `iso` has passed: the client
 *  never advances a timer's outcome itself, it re-reads the snapshot. */
export function useRefetchAt(iso: string | null | undefined, refetch: () => unknown, graceMs = 300): void {
  const fn = useRef(refetch)
  fn.current = refetch
  useEffect(() => {
    if (!iso) return
    const due = Date.parse(iso) + graceMs - serverClock.now()
    const t = setTimeout(() => fn.current(), Math.max(0, Math.min(due, 2_147_000_000)))
    return () => clearTimeout(t)
  }, [iso, graceMs])
}

/** Seconds left to a server moment and the share passed since `from` (for timers). */
export function countdown(now: number, until: string, from?: string | null): { ms: number; text: string; passed: number } {
  const end = Date.parse(until)
  const ms = Math.max(0, end - now)
  const start = from ? Date.parse(from) : NaN
  const passed = Number.isFinite(start) && end > start ? Math.min(1, Math.max(0, (now - start) / (end - start))) : 0
  return { ms, text: durationText(ms), passed }
}
